import type { Cluster } from "../../../lib/types";
import { MIN_COHORT_SIZE } from "../../../lib/cohorts";

type Col = { cohort: string; title: string; count: number; accent: boolean };

/** The "X sagen" cluster columns under a scale hero or chart: promoters/detractors
 *  (NPS), low/high (Likert), MCQ "other", polar side_a/side_b. Ported unchanged
 *  from the former NPS stage view. */
export function CohortClusterColumns({
  columns,
  clusters,
}: {
  columns: Col[];
  clusters: Cluster[];
}) {
  return (
    <div className="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-2 gap-5">
      {columns.map((col) => {
        const cs = clusters.filter((c) => c.cohort === col.cohort);
        return (
          <section
            key={col.cohort}
            className="flex flex-col gap-3 min-h-0 animate-fade-up"
          >
            <header className="flex items-center gap-3">
              <span
                aria-hidden
                className={`w-1 h-5 rounded-full ${col.accent ? "bg-rc-berry" : "bg-rc-paper/40"}`}
              />
              <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-rc-paper/85">
                {col.title}
              </span>
              <span className="ml-auto text-[12px] tabular-nums text-rc-paper/55">
                {col.count} {col.count === 1 ? "Antwort" : "Antworten"}
              </span>
            </header>
            {col.count < MIN_COHORT_SIZE ? (
              <Placeholder
                label={`Noch zu wenige Antworten (${col.count}/${MIN_COHORT_SIZE}).`}
              />
            ) : cs.length === 0 ? (
              <Placeholder label="Cluster werden berechnet…" />
            ) : (
              <ul className="flex flex-col gap-2 overflow-y-auto pr-1">
                {cs.map((c) => (
                  <li
                    key={c.id}
                    className={`flex items-baseline justify-between gap-3 px-4 py-3 rounded-rc-lg border ${col.accent ? "bg-rc-berry/[0.08] border-rc-berry/25" : "bg-rc-paper/[0.04] border-rc-paper/[0.12]"}`}
                  >
                    <span className="text-[15px] font-semibold leading-tight text-rc-paper">
                      {c.label}
                    </span>
                    <span className="text-[12px] tabular-nums text-rc-paper/55 flex-shrink-0">
                      {c.size}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Placeholder({ label }: { label: string }) {
  return (
    <div className="rounded-rc-lg border border-dashed border-rc-paper/[0.12] px-4 py-5 text-[13px] text-rc-paper/45 italic">
      {label}
    </div>
  );
}
